import { CalendarDays, Camera, CircleAlert, CircleCheck } from 'lucide-react';
import { Reveal } from '@/components/ui/reveal';

// The full 6-point condition checklist. Every other bit of copy on the page
// promises six points, so this section has to show all six.
const MOCK_CHECKS = [
  { label: 'Encroachment (Kabzaa)', status: 'Clear', ok: true },
  { label: 'Legal notices & claims', status: 'Clear', ok: true },
  { label: 'Waste dumping', status: 'Clear', ok: true },
  { label: 'Drainage', status: 'Needs attention', ok: false },
  { label: 'Waterlogging', status: 'Clear', ok: true },
  { label: 'Vegetation overgrowth', status: 'Trimming advised', ok: false },
];

// TODO(#202): self-host these on the same S3/CloudFront distribution and
// replace with real inspection photos instead of generic Unsplash stock.
const MOCK_PHOTOS = [
  'https://images.unsplash.com/photo-1597372811123-d0f1795bbdf9?q=80&w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&q=80&w=400&h=300',
];

/**
 * The proof column lifted out of the hero into its own anchored section, so the
 * hero can run full-height and single-column. Set as a document, not a floating
 * card: no frame, no rotation, no corner pegs — hairlines and space carry the
 * structure. `id="sample-report"` is the target of the hero's second CTA.
 */
export function SampleReport() {
  return (
    // Small scroll-mt only: the section's own top padding already supplies the
    // air above the heading when the hero's "See Sample Report" jumps here.
    // Matching scroll-mt to that padding would double it to ~250px of blank.
    <section id="sample-report" className="scroll-mt-2">
      <Reveal className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20 md:py-28">
        {/* Stacks below 640px — side-by-side put the plot line against the screen edge. */}
        <div className="flex flex-col gap-2 border-b border-foreground/20 pb-5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
          <h2 className="font-display text-2xl font-medium leading-tight tracking-tight text-foreground md:text-3xl">
            Sample inspection report
          </h2>
          <p className="flex items-center gap-2 text-sm text-foreground/65 sm:text-base">
            <CalendarDays className="size-4 shrink-0" />
            Inspected 4 June 2026 · Plot 42, Kokapet, Hyderabad
          </p>
        </div>

        <div className="mt-8 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
          {MOCK_PHOTOS.map((src) => (
            <img
              key={src}
              src={src}
              alt="Site photo from inspection"
              className="h-40 w-full object-cover sm:h-64 md:h-80"
              width={400}
              height={300}
              /* Below the fold now that the hero owns the first screen. */
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
        <p className="mt-4 flex items-center gap-2 text-sm text-foreground/65 sm:text-base">
          <Camera className="size-4 shrink-0" />
          12 photos · 2 videos captured on site
        </p>

        <ul className="mt-12 border-t border-line-strong">
          {MOCK_CHECKS.map((check) => (
            <li
              key={check.label}
              className="flex items-center justify-between gap-4 border-b border-border py-4 sm:py-5"
            >
              <span className="text-foreground/80 sm:text-lg">{check.label}</span>
              <span
                className={`flex shrink-0 items-center gap-2 text-sm font-medium sm:text-base ${
                  check.ok ? 'text-sidebar-primary' : 'text-ring'
                }`}
              >
                {check.ok ? (
                  <CircleCheck className="size-4 shrink-0 sm:size-5" />
                ) : (
                  <CircleAlert className="size-4 shrink-0 sm:size-5" />
                )}
                {check.status}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-sm text-foreground/60">
          Illustrative layout. Actual reports include real on-site photos and videos of your plot.
        </p>
      </Reveal>
    </section>
  );
}
